// QuickFormModal — a small reusable create/edit modal driven by a field list.
// Used to wire the ITSM "Add"/"New"/"Invite" actions to real write endpoints
// without a bespoke modal per page.

import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';

export interface QuickField {
  key: string;
  label: string;
  type?: 'text' | 'email' | 'password' | 'number' | 'select' | 'textarea';
  required?: boolean;
  placeholder?: string;
  options?: { value: string; label: string }[];
  defaultValue?: string;
}

export function QuickFormModal({
  title, fields, submitLabel, onClose, onSubmit,
}: {
  title: string;
  fields: QuickField[];
  submitLabel?: string;
  onClose: () => void;
  onSubmit: (values: Record<string, string>) => Promise<void>;
}) {
  const { t } = useTranslation('components');
  const [values, setValues] = useState<Record<string, string>>(
    () => Object.fromEntries(fields.map((f) => [f.key, f.defaultValue ?? ''])),
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const set = (k: string, v: string) => setValues((s) => ({ ...s, [k]: v }));
  const missing = fields.some((f) => f.required && !values[f.key]?.trim());

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (missing) { setError(t('quickFormModal.errors.required')); return; }
    setBusy(true); setError(null);
    try {
      await onSubmit(values);
      onClose();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string; message?: string } } })?.response?.data;
      setError(msg?.message ?? msg?.error ?? t('quickFormModal.errors.generic'));
      setBusy(false);
    }
  };

  return (
    <div className="tk-modal-overlay" onClick={onClose}>
      <div className="tk-modal w-full max-w-md p-5" onClick={(e) => e.stopPropagation()}>
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-base font-semibold">{title}</h2>
          <button className="tk-btn-ghost px-2" onClick={onClose} aria-label={t('quickFormModal.actions.close')}><X size={16} /></button>
        </div>
        <form onSubmit={submit} className="space-y-3">
          {fields.map((f) => (
            <div key={f.key}>
              <label className="mb-1 block text-sm text-muted">
                {f.label}{f.required && <span className="text-critical"> *</span>}
              </label>
              {f.type === 'select' ? (
                <select
                  className="tk-input"
                  value={values[f.key]} onChange={(e) => set(f.key, e.target.value)}
                >
                  <option value="">{t('quickFormModal.selectPlaceholder')}</option>
                  {(f.options ?? []).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              ) : f.type === 'textarea' ? (
                <textarea
                  className="tk-input"
                  rows={3} value={values[f.key]} onChange={(e) => set(f.key, e.target.value)} placeholder={f.placeholder}
                />
              ) : (
                <input
                  className="tk-input"
                  type={f.type ?? 'text'} value={values[f.key]} onChange={(e) => set(f.key, e.target.value)}
                  placeholder={f.placeholder} autoFocus={f === fields[0]}
                />
              )}
            </div>
          ))}
          {error && <p className="text-sm text-critical">{error}</p>}
          <div className="flex justify-end gap-2 pt-1">
            <button type="button" className="tk-btn-ghost" onClick={onClose} disabled={busy}>{t('quickFormModal.actions.cancel')}</button>
            <button type="submit" className="tk-btn-primary" disabled={busy || missing}>{busy ? t('quickFormModal.actions.saving') : (submitLabel ?? t('quickFormModal.actions.create'))}</button>
          </div>
        </form>
      </div>
    </div>
  );
}
